import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhotoPicker } from "../equipment/shared/PhotoPicker";
import { HoManualEntryFields, todayLocal } from "./shared/HoManualEntryFields";

const BULLETS_KINDS = [
  { key: "received", label: "Bullets Received" },
  { key: "replacement", label: "Bullets Replacement" },
];

export default function BulletsItemForm({ kind: kindProp, dayId, initial, onClose, onSaved, manual = false, sites = [] }) {
  const isEdit = !!initial;
  const [kind, setKind] = useState(kindProp);
  const [manualSite, setManualSite] = useState("");
  const [entryDate, setEntryDate] = useState(todayLocal());
  const [bulletType, setBulletType] = useState(initial?.bullet_type ?? "");
  const [toolUsed, setToolUsed] = useState(initial?.tool_used ?? "");
  const [quantity, setQuantity] = useState(initial?.quantity ?? initial?.number_of_bullets ?? "");
  const [remarks, setRemarks] = useState(initial?.remarks ?? "");
  const [photos, setPhotos] = useState([]);
  const [oldPhotos, setOldPhotos] = useState([]);
  const [newPhotos, setNewPhotos] = useState([]);
  const [options, setOptions] = useState({ bullet_types: [], tool_used_options: [] });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    client
      .get("/bullets-register/bullet-types")
      .then((res) => setOptions(res.data))
      .catch((err) => toast.error(getErrorMessage(err)));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (manual && !manualSite) return toast.error("Select a site");
    if (manual && !entryDate) return toast.error("Entry Date is required");
    if (manual && entryDate > todayLocal()) return toast.error("Entry Date cannot be in the future");
    if (!bulletType) return toast.error("Select Bullet Type");
    const qty = parseFloat(quantity);
    if (!(qty > 0)) return toast.error(kind === "received" ? "Quantity must be greater than 0" : "Number of Bullets must be greater than 0");
    if (kind === "replacement" && !isEdit && !toolUsed) return toast.error("Select Tool Used");
    if (!isEdit && kind === "received" && photos.length === 0) return toast.error("At least one photo is required");
    if (!isEdit && kind === "replacement" && oldPhotos.length === 0) return toast.error("Old Bullet Photo is required");
    if (!isEdit && kind === "replacement" && newPhotos.length === 0) return toast.error("New Bullet Photo is required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("bullet_type", bulletType);
      if (manual) {
        fd.append("site", manualSite);
        fd.append("entry_date", entryDate);
      }
      if (kind === "replacement") {
        fd.append("tool_used", toolUsed);
        fd.append("number_of_bullets", qty);
        oldPhotos.forEach((p) => fd.append("old_photos", p));
        newPhotos.forEach((p) => fd.append("new_photos", p));
      } else {
        fd.append("quantity", qty);
        photos.forEach((p) => fd.append("photos", p));
      }
      if (remarks.trim()) fd.append("remarks", remarks.trim());

      const url = isEdit
        ? `/bullets-register/day/${dayId}/${kind}/${initial.item_id}`
        : manual
          ? `/bullets-register/manual/${kind}`
          : dayId
            ? `/bullets-register/day/${dayId}/${kind}`
            : `/bullets-register/${kind}`;
      const res = isEdit ? await client.put(url, fd) : await client.post(url, fd);
      if (res.data.negative_balance_warning) {
        toast.warning(`Bullets balance has gone negative for ${res.data.negative_balance_type || bulletType}. Please verify the entries.`);
      }
      toast.success(isEdit ? "Entry updated" : "Entry added");
      onSaved(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bullets-item-form">
        <DialogHeader>
          <DialogTitle>
            {manual ? "Add entry (HO)" : `${isEdit ? "Edit" : "Add"} ${kind === "received" ? "Bullets Received" : "Bullets Replacement"}`}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {manual && (
            <HoManualEntryFields prefix="bullets" sites={sites} site={manualSite} onSite={setManualSite} date={entryDate} onDate={setEntryDate} kinds={BULLETS_KINDS} kind={kind} onKind={setKind} />
          )}
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Bullet Type *</p>
            <SearchableSelect testId="bullets-type-select" value={bulletType} onValueChange={setBulletType} placeholder="Select bullet type"
  options={options.bullet_types.map((t) => ({ value: t, label: t, testId: `bullets-type-option-${t}` }))} />
          </div>
          {kind === "replacement" && (
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1.5">Tool Used *</p>
              <SearchableSelect testId="bullets-tool-select" value={toolUsed} onValueChange={setToolUsed} placeholder="Select tool"
  options={options.tool_used_options.map((t) => ({ value: t, label: t, testId: `bullets-tool-option-${t}` }))} />
            </div>
          )}
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">{kind === "received" ? "Quantity *" : "Number of Bullets *"}</p>
            <Input type="number" step="any" data-testid="bullets-quantity-input" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Remarks</p>
            <Input data-testid="bullets-remarks-input" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </div>
          {kind === "received" ? (
            <PhotoPicker label={isEdit ? "Add Photos (optional)" : "Photos"} files={photos} onChange={setPhotos} testId="bullets-photos-input" required={!isEdit} />
          ) : (
            <>
              <PhotoPicker label={isEdit ? "Add Old Bullet Photos (optional)" : "Old Bullet Photos"} files={oldPhotos} onChange={setOldPhotos} testId="bullets-old-photos-input" required={!isEdit} />
              <PhotoPicker label={isEdit ? "Add New Bullet Photos (optional)" : "New Bullet Photos"} files={newPhotos} onChange={setNewPhotos} testId="bullets-new-photos-input" required={!isEdit} />
            </>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="bullets-item-submit" className="bg-amber-600 hover:bg-amber-700">
              {busy ? "Saving..." : isEdit ? "Save Changes" : "Add Entry"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
